'use client';

import type {
  FormPayload,
  GatewaySubmitPayload,
  SubmitFormResult,
} from '../core';

const TRAILING_GATEWAY_SLASH_PATTERN = /\/+(?=($|[?#]))/;
const DEFAULT_GATEWAY_URL = '/bx-capterra-api/bx-forms/submit';

function normalizeGatewayUrl(gatewayUrl: string): string {
  const trimmedGatewayUrl = gatewayUrl.trim();
  const normalizedGatewayUrl = trimmedGatewayUrl.replace(
    TRAILING_GATEWAY_SLASH_PATTERN,
    ''
  );

  return normalizedGatewayUrl ? normalizedGatewayUrl : DEFAULT_GATEWAY_URL;
}

/**
 * Submit form data to the gateway route.
 * This is a client-side function that calls /bx-capterra-api/bx-forms/submit.
 *
 * @param payload - The form submission payload containing formId, formTemplate, tracking, and formData
 * @param gatewayUrl - Optional custom gateway URL (defaults to /bx-capterra-api/bx-forms/submit)
 * @returns Promise resolving to SubmitFormResult with success status and optional error
 *
 * @example
 * ```typescript
 * import { submitToGateway } from 'bx-forms/browser';
 *
 * const result = await submitToGateway({
 *   formId: 'contentful-form-id',
 *   formTemplate: 'eas-comparison-flow',
 *   tracking: { brand: 'capterra', pageName: 'directory' },
 *   formData: { emailAddress: 'user@example.com' },
 * });
 *
 * if (result.success) {
 *   console.log('Form submitted successfully');
 * } else {
 *   console.error('Submission failed:', result.error);
 * }
 * ```
 */
export async function submitToGateway(
  payload: GatewaySubmitPayload,
  gatewayUrl = DEFAULT_GATEWAY_URL
): Promise<SubmitFormResult> {
  try {
    const response = await fetch(normalizeGatewayUrl(gatewayUrl), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || `Request failed with status ${response.status}`,
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to submit form',
    };
  }
}

/**
 * Create a submit function that uses the gateway route.
 * This is a drop-in replacement for server actions.
 *
 * @param gatewayUrl - Optional custom gateway URL (defaults to /bx-capterra-api/bx-forms/submit)
 * @returns A function that can be passed to InlineForm's submitAction prop
 *
 * @example
 * ```tsx
 * import { createGatewaySubmitAction } from 'bx-forms/browser';
 *
 * const submitAction = createGatewaySubmitAction();
 * // or with custom URL:
 * const submitAction = createGatewaySubmitAction('https://www.capterra.com/bx-capterra-api/bx-forms/submit');
 *
 * <InlineForm submitAction={submitAction} ... />
 * ```
 */
export function createGatewaySubmitAction(gatewayUrl = DEFAULT_GATEWAY_URL) {
  return async (payload: FormPayload): Promise<SubmitFormResult> => {
    return await submitToGateway(
      {
        formId: payload.formId,
        formTemplate: payload.formTemplate,
        tracking: payload.tracking,
        formData: payload.formData,
        submissionMetadata: payload.submissionMetadata,
      },
      gatewayUrl
    );
  };
}
